import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Eye = styled.div`
  width: 100%;
  height: 0;
  overflow: hidden;
  position: relative;
  padding-top: 50%;
`;

const EyeWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const RowWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const EyeRow = styled.div`
  --y: -22%;
  margin-top: -3.25%;
  width: 125vw;
  max-width: 210rem;
  display: flex;
  transform: translate(0, var(--y));

  &:nth-of-type(2n) {
    transform: translate(-10%, var(--y));
  }
`;

const EyeSVGWrapper = styled.svg`
  margin: 0 2%;
  transform: translateZ(0px);
  transform-style: preserve-3d;
  will-change: transform;
  width: 100%;
  height: 100%;
  visibility: hidden;
`;

const EyeSVG = React.forwardRef(({ fill = 'var(--c-purple)' }, ref) => {
  return (
    <EyeSVGWrapper ref={ref} viewBox="0 0 763.5 504.14" fill={fill}>
      <path d="M474.22,168.54C600.6,195.66,727.48,264.62,744.89,274.3a131.15,131.15,0,0,0-7.41-23.49C716.1,239.53,677.32,220,630.23,200.6,560,171.69,496.84,153.16,441.42,145.12c125.72-7.8,258.3,43,270.41,47.78a114.88,114.88,0,0,0-13.31-23.2,832.06,832.06,0,0,0-103.3-28.6C546.5,131,481.52,122.54,418.39,130c47.22-13.26,112.53-24.4,191.7-19.66a116.3,116.3,0,0,0-25.4-18.1c-107.18-2.06-186.08,23.13-227.33,40.55,30.24-24.12,82.83-57,163.31-78.79v0a121.68,121.68,0,0,0-31.27-8.5c-92.34,29.75-145.66,72.87-172.21,100.43,21.49-40.58,53.63-88.2,100.36-130.65A147.17,147.17,0,0,0,390,17.85c-61.39,61.28-95.3,128.69-112.51,172.53,3.06-51.07,12.9-119.23,41.27-173a118.15,118.15,0,0,0-23,8.11c-37.15,83-37.71,186.79-36.44,225C239.88,211.11,215.87,145,213,55a136.42,136.42,0,0,0-16.27,18.17c6.21,100,36.77,168.56,56.79,203.52C158.4,189.14,121,116.27,118.94,112.23a145,145,0,0,0-7.4,22.08c17.93,30.47,67.11,103.88,165,185.28-68.25-31.68-118.18-66.39-150.4-92.53-32.85-26.66-53-48.77-61.64-59a129,129,0,0,0-2.26,23.35c30,32.29,102,98.86,227.22,152.78C163.14,317.08,36.26,248.12,18.85,238.44a131.31,131.31,0,0,0,7.41,23.48c21.38,11.29,60.16,30.82,107.25,50.22,70.18,28.91,133.39,47.43,188.82,55.47-125.74,7.8-258.34-43-270.43-47.78A114.92,114.92,0,0,0,65.22,343a829.18,829.18,0,0,0,103.29,28.61c35.79,7.44,80.35,14,126.5,14a431.38,431.38,0,0,0,50.8-2.95C298.56,396,233.08,407.21,153.65,402.42a116.3,116.3,0,0,0,25.4,18.1c4.17.08,8.36.18,12.44.18,100.88,0,175.22-24,214.86-40.72-30.25,24.12-82.83,57-163.28,78.78v0a122,122,0,0,0,31.27,8.51C366.7,437.54,420,394.42,446.57,366.86c-21.49,40.58-53.63,88.21-100.37,130.66a148,148,0,0,0,27.53-2.63c61.41-61.3,95.33-128.74,112.52-172.58-3,51.06-12.89,119.22-41.29,173.07a118,118,0,0,0,23-8.12c37.15-83,37.71-186.79,36.44-225,19.43,39.35,43.44,105.44,46.28,195.49A136.35,136.35,0,0,0,567,439.6c-6.21-100-36.78-168.57-56.79-203.53,95.1,87.48,132.55,160.32,134.61,164.43a145.26,145.26,0,0,0,7.4-22.07c-17.91-30.43-67-103.67-164.62-184.91-.05-.08-.1-.15-.14-.23,68.1,31.64,117.94,66.28,150.11,92.39,32.85,26.66,53,48.76,61.64,59a129.13,129.13,0,0,0,2.26-23.36C671.46,289,599.4,222.45,474.22,168.54Zm-91.07,189.7A107.63,107.63,0,1,1,490.78,250.61,107.75,107.75,0,0,1,383.15,358.24Z" />
    </EyeSVGWrapper>
  );
});

const EyePatternTracking = () => {
  let request;
  const wrapper = useRef(null);
  const eyes = useRef([]);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    gsap.from(eyes.current, {
      stagger: {
        amount: 0.7,
        from: 'random',
        grid: 'auto',
      },
      transformOrigin: 'center center',
      autoAlpha: 0,
      scale: 0.5,
      ease: 'back.out(.8)',
      scrollTrigger: {
        trigger: wrapper.current,
        start: 'top 80%',
      },
    });
  }, []);

  function onMouseLeave() {
    cancelAnimationFrame(request);

    gsap.to(eyes.current, {
      duration: 1,
      transform: `rotate3d(0, 0, 0, 0deg)`,
    });
  }

  function onMouseMove(e) {
    const x = e.nativeEvent.x;
    const y = e.nativeEvent.y;

    mouse.current = { x, y };

    cancelAnimationFrame(request);
    request = requestAnimationFrame(update);
  }

  function update() {
    for (let eye of eyes.current) {
      if (eye === null) return;

      const eyeRect = eye.getBoundingClientRect();
      const eyeX =
        eyeRect.left >= 0 ? eyeRect.left : Math.abs(eyeRect.left) * -0.1;
      const eyeY = eyeRect.top;
      const centerX = eyeX + eyeRect.width / 2;
      const centerY = eyeY + eyeRect.height / 2;
      const dX = mouse.current.x - centerX;
      const dY = mouse.current.y - centerY;

      const transformY = 30;
      const transformX = 30;
      const rotate = 30;

      const tiltX = (dY / centerY) * 10;
      const tiltY = -(dX / centerX) * 10;
      const radius = Math.sqrt(Math.pow(tiltX, 2) + Math.pow(tiltY, 2));
      const degree = radius * 40;

      gsap.to(eye, {
        transform: `rotate3d(${gsap.utils.clamp(
          -transformX,
          transformX,
          tiltX
        )}, ${gsap.utils.clamp(
          -transformY,
          transformY,
          tiltY
        )}, 0, ${gsap.utils.clamp(-rotate, rotate, degree)}deg)`,
        transformOrigin: 'center center',
      });
    }
  }

  return (
    <Eye ref={wrapper}>
      <EyeWrapper
        viewBox="0 0 2046.28 1118.62"
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
      >
        {/*
            Not happy with this repetition, but couldn't get the same
            performance using loops
        */}
        <RowWrapper>
          <EyeRow>
            {[...Array(5)].map((_, i) => (
              <EyeSVG key={i} />
            ))}
          </EyeRow>
          <EyeRow>
            {[...Array(5)].map((_, i) => (
              <EyeSVG key={i} ref={el => eyes.current.push(el)} />
            ))}
          </EyeRow>
          <EyeRow>
            {[...Array(5)].map((_, i) => (
              <EyeSVG key={i} ref={el => eyes.current.push(el)} />
            ))}
          </EyeRow>
          <EyeRow>
            {[...Array(5)].map((_, i) => (
              <EyeSVG key={i} ref={el => eyes.current.push(el)} />
            ))}
          </EyeRow>
          <EyeRow>
            {[...Array(5)].map((_, i) => (
              <EyeSVG
                key={i}
                fill={(i + 1) % 3 === 0 ? 'var(--c-white)' : 'var(--c-purple)'}
                ref={el => eyes.current.push(el)}
              />
            ))}
          </EyeRow>
          <EyeRow>
            {[...Array(5)].map((_, i) => (
              <EyeSVG key={i} ref={el => eyes.current.push(el)} />
            ))}
          </EyeRow>
          <EyeRow>
            {[...Array(5)].map((_, i) => (
              <EyeSVG key={i} ref={el => eyes.current.push(el)} />
            ))}
          </EyeRow>
          <EyeRow>
            {[...Array(5)].map((_, i) => (
              <EyeSVG key={i} ref={el => eyes.current.push(el)} />
            ))}
          </EyeRow>
        </RowWrapper>
      </EyeWrapper>
    </Eye>
  );
};

export default EyePatternTracking;
